/**
 * Texte der Etiketten (Story 27.2, AD-42): Fehlersätze der Commands und Routen, Namen der Anlässe, Startvorlagen,
 * System- und Anlass-Felder. Browser-sicher, über `@till-erp/documents/values` exportiert.
 */
import type { LabelBarcodeFormat, LabelSegment } from './design.ts';

type LabelDateFormat = Extract<LabelSegment, { kind: 'date' }>['format'];

const fmtMm = (v: number) => String(Math.round(v * 10) / 10).replace('.', ',');

export const LABEL_TEXTS = {
  // Rechte (labels/access.ts)
  forbiddenDesign: 'Etikett-Vorlagen gestalten die Rollen Benutzer, Packplatz und Admin.',
  forbiddenAdmin: 'Nur Admins legen Druckerprofile und Standard-Vorlagen fest.',
  forbiddenAi: 'Etikett-Vorlagen legt nur ein Mensch an; die KI gestaltet keine Etiketten.',
  forbiddenPrint: 'Etiketten drucken die Rollen Benutzer, Packplatz und Admin.',
  forbiddenPrintAi: 'Etiketten druckt nur ein Klick eines Menschen; die KI druckt nie.',
  // Vorlagen
  templateMissing: 'Diese Etikett-Vorlage gibt es nicht.',
  templateChanged: 'Die Vorlage wurde inzwischen geändert. Bitte neu laden; Ihre Änderung ist noch nicht gespeichert.',
  templateArchived: 'Diese Vorlage ist archiviert und lässt sich nicht mehr ändern. Bitte duplizieren und das Duplikat bearbeiten.',
  alreadyArchived: 'Diese Vorlage ist schon archiviert.',
  archiveIsDefault: (occasions: string[]) => `Diese Vorlage ist Standard für ${occasions.map((o) => `„${o}“`).join(', ')} – bitte zuerst einen anderen Standard wählen.`,
  versionMissing: (version: number) => `Die Fassung ${version} dieser Vorlage gibt es nicht.`,
  versionIsCurrent: (version: number) => `Fassung ${version} ist schon die aktuelle Fassung.`,
  nameMissing: 'Bitte einen Namen für die Vorlage angeben (höchstens 100 Zeichen).',
  sizeMismatch: (w: number, h: number) => `Das Design hat nicht die Größe der Vorlage (${fmtMm(w)} × ${fmtMm(h)} mm).`,
  sheetLayoutMissing: 'Für A4-Bögen bitte das Bogen-Layout angeben (Spalten, Zeilen, Ränder, Abstände).',
  sheetLayoutOnlySheet: 'Ein Bogen-Layout gibt es nur bei Bögen, nicht bei Rollen.',
  sheetLayoutTooSmall: 'Die Etiketten passen mit diesen Spalten, Zeilen, Rändern und Abständen nicht auf den Bogen.',
  starterWrongKind: 'Diese Startvorlage passt nicht zur Art des Etiketts.',
  // Design (Zod-Schema label-design v1)
  designInvalid: 'Das Design der Vorlage ist so nicht speicherbar. Bitte die markierten Angaben prüfen.',
  designNotObject: 'Das Design fehlt oder ist kein Objekt.',
  elementUnknown: (type: string) => `Ein Element der Art „${type}“ gibt es nicht. Möglich sind Text, Strichcode, Linie, Rahmen, Ellipse und Bild.`,
  segmentUnknown: (kind: string) => `Einen Baustein „${kind}“ gibt es nicht. Möglich sind fester Text, Feld, Datum, Uhrzeit und Zähler.`,
  unknownProperty: (keys: string[]) => `Unbekannte Angabe ${keys.map((k) => `„${k}“`).join(', ')}.`,
  valueMissing: 'Hier fehlt eine Angabe.',
  valueWrongType: 'Diese Angabe hat die falsche Form.',
  valueChoice: (options: string[]) => `Bitte einen dieser Werte wählen: ${options.join(', ')}.`,
  valueTooSmall: (min: number | bigint) => `Der Wert ist zu klein (mindestens ${String(min).replace('.', ',')}).`,
  valueTooBig: (max: number | bigint) => `Der Wert ist zu groß (höchstens ${String(max).replace('.', ',')}).`,
  textTooLong: (max: number | bigint) => `Bitte höchstens ${String(max)} Zeichen.`,
  textTooShort: 'Diese Angabe ist zu kurz.',
  sizeLimitsOrder: 'Die kleinste Etikettgröße darf nicht größer sein als die größte.',
  tooManyElements: (max: number | bigint) => `Ein Etikett hat höchstens ${String(max)} Elemente.`,
  listTooShort: 'Hier fehlt mindestens ein Baustein.',
  stepTenth: 'Größen bitte in Schritten von 0,1 mm angeben.',
  duplicateId: 'Diese Element-Kennung kommt mehrfach vor; jede Kennung nur einmal.',
  outside: (w: number, h: number) => `Das Element ragt über das Etikett hinaus (${fmtMm(w)} × ${fmtMm(h)} mm).`,
  fieldKeyFormat: 'Ein Feldschlüssel hat die Form „modul.feld“, z. B. „catalog.number“.', // sql-lint: erlaubt Beispiel eines Feldschlüssels, kein SQL
  fieldUnknown: (key: string) => `Das Feld „${key}“ gibt es nicht. Bitte ein Feld aus der Liste links wählen.`,
  fieldOnlyArticle: (key: string) => `Das Feld „${key}“ gibt es nur auf Artikel-Etiketten (Wareneingang).`,
  fieldsKindInvalid: 'Bitte die Art „article“ (Artikel) oder „bin“ (Lagerplatz) angeben.',
  // Vorschau im Editor (Story 27.5)
  sampleMissing: 'Diesen Artikel bzw. Lagerplatz gibt es nicht (mehr). Bitte einen anderen für die Vorschau wählen.',
  // Standard je Anlass
  defaultWrongKind: (occasion: string) => `Für den Anlass „${occasion}“ passt nur eine Vorlage dieser Art.`,
  defaultArchived: 'Eine archivierte Vorlage kann kein Standard sein.',
  // Bilder
  imageNotPng: 'Bitte ein Bild im Format PNG hochladen.',
  imageTooBig: 'Das Bild ist zu groß (höchstens 256 KB).',
  imageEmpty: 'Das Bild ist leer.',
  imageDimensions: 'Das PNG ist beschädigt oder zu groß (höchstens 10.000 × 10.000 Punkte).',
  imageMissing: 'Dieses Bild gibt es nicht.',
  imageMissingInDesign: 'Dieses Bild gibt es nicht (mehr). Bitte das Bild neu hochladen oder ein anderes wählen.',
  imageNameMissing: 'Bitte einen Namen für das Bild angeben (höchstens 100 Zeichen).',
  // Druck (Story 27.6)
  printItemsMissing: 'Bitte mindestens einen Artikel mit einer Menge angeben.',
  printQuantityInvalid: 'Bitte eine ganze Zahl ab 1 als Menge angeben.',
  printTooMany: (max: number) => `Höchstens ${max.toLocaleString('de-DE')} Etiketten je Druck. Bitte weniger wählen oder in mehreren Drucken drucken.`,
  printDuplicateItem: 'Jeder Artikel nur einmal je Druck; bitte die Menge erhöhen.',
  printPrinterMissing: 'Dieses Druckerprofil gibt es nicht (mehr). Bitte einen anderen Drucker wählen.',
  printPrinterChanged: 'Das Druckerprofil wurde inzwischen geändert. Bitte noch einmal drucken.',
  printNoPrinters: 'Noch kein Etikettendrucker eingerichtet. Druckerprofile legt der Admin unter Einstellungen › Drucken › Drucker an.',
  printTemplateWrongKind: 'Für Artikel-Etiketten passt nur eine Vorlage der Art „Artikel“.',
  printTemplateArchived: 'Diese Vorlage ist archiviert und druckt nicht mehr. Bitte eine andere Vorlage wählen.',
  printTemplateRequired: 'Bitte eine Vorlage wählen.',
  printTestOnlyCalibration: 'Ein Testdruck hat keine Vorlage; ein Druck mit Vorlage ist kein Testdruck.',
  printWayInvalid: 'Diesen Anlass gibt es nicht.',
  printArticleMissing: 'Diesen Artikel gibt es nicht (mehr).',
  printDeviceMissing: 'Das Gerät fehlt. Bitte die Seite neu laden und noch einmal drucken.',
  printMissing: 'Diesen Druck gibt es nicht.',
  printOtherDevice: 'Dieser Druck wurde an einem anderen Gerät gestartet und lässt sich nur dort bestätigen.',
  printExpired: 'Die Frist für diesen Druck ist abgelaufen – das Druckergebnis ist unklar. Bitte am Drucker nachsehen.',
  printSettled: 'Dieser Druck ist schon abgeschlossen.',
  printErrorMissing: 'Bitte angeben, warum der Druck nicht übergeben wurde.',
  // Grund eines nicht übergebenen Drucks (fester Code statt Freitext, AD-11); der Satz steht im Druckverlauf.
  printFailQzMissing: 'QZ Tray läuft nicht. QZ Tray am PC starten, dann erneut drucken.',
  printFailPrinterMissing: (printer: string) => `Der Drucker „${printer}“ ist in QZ Tray nicht zu finden. Drucker einschalten oder den Namen unter Einstellungen › Drucken › Drucker prüfen, dann erneut drucken.`,
  printFailQzRefused: 'QZ Tray hat den Druck nicht angenommen. Drucker prüfen, dann erneut drucken.',
  printRetryInvalid: '„Erneut drucken“ geht nur für einen eigenen Druck, der nicht gedruckt wurde.',
  printRenderFailed: 'Das Etikett lässt sich so nicht drucken. Bitte die Vorlage im Editor prüfen.',
  printSheetNeedsPdf: 'A4-Bögen druckt nur ein PDF-Drucker. Bitte einen PDF-Drucker wählen oder eine Rollen-Vorlage.',
  printStartCellInvalid: 'Bitte das erste freie Feld des Bogens als ganze Zahl ab 1 angeben.',
  printTestSizeInvalid: (min: number, max: number) => `Bitte eine Größe von ${min} bis ${max} mm angeben.`,
  printTestSizeSettingMissing: 'Einstellung der Testdruck-Größe fehlt (Migration 0562).',
  printSettingMissing: 'Einstellung des Etikettendrucks fehlt (Migration 0562).',
  // Einstellungen
  printerOffsetInvalid: 'Bitte einen Wert von −10 bis 10 mm mit höchstens einer Nachkommastelle eingeben, z. B. „0,5“ oder „-1“.',
} as const;

/** Status eines Drucks (Druckprotokoll, AD-42): `handed_over` heißt nur „an QZ Tray übergeben“, kein Papier-Nachweis. */
export const labelPrintStatusLabels = {
  queued: 'Wartet auf Druckplatz',
  claimed: 'Wird übergeben',
  handed_over: 'An Drucker übergeben',
  failed: 'Nicht gedruckt',
  unclear: 'Druckergebnis unklar',
} as const;

/**
 * Kurze Status-Wörter für die Pille im Druckverlauf (Sichtung HS55: Pille ganz lesbar bei 1440 px). „Übergeben“ heißt
 * wie oben nur „an QZ Tray übergeben“; der Druckdialog sagt es ausführlich.
 */
export const labelPrintStatusShortLabels = {
  queued: 'Wartet',
  claimed: 'Wird übergeben',
  handed_over: 'Übergeben',
  failed: 'Nicht gedruckt',
  unclear: 'Ergebnis unklar',
} as const satisfies Record<keyof typeof labelPrintStatusLabels, string>;

/** Anlass eines Drucks im Druckverlauf (Anlässe der Standard-Vorlage plus Testdruck). */
export const labelPrintWayLabels = {
  goods_receipt: 'Wareneingang',
  article_list: 'Artikelliste',
  article: 'Artikelansicht',
  bin: 'Lagerplatz',
  test: 'Testdruck',
} as const;

/** Anlässe für die Standard-Vorlage (Einstellung `label_default_templates`). */
export const labelOccasions = ['goods_receipt', 'article_list', 'article', 'bin'] as const;
export type LabelOccasion = (typeof labelOccasions)[number];
export const labelOccasionLabels: Record<LabelOccasion, string> = {
  goods_receipt: 'Wareneingang',
  article_list: 'Artikelliste',
  article: 'Artikelansicht',
  bin: 'Lagerplatz',
};
/** Art der Vorlage, die zu einem Anlass passt. */
export const labelOccasionKind: Record<LabelOccasion, 'article' | 'bin'> = { goods_receipt: 'article', article_list: 'article', article: 'article', bin: 'bin' };

export const labelKindLabels = { article: 'Artikel', bin: 'Lagerplatz' } as const;

/** System-Felder (für jede Art). Datum, Uhrzeit und Zähler sind eigene Segmente, keine Felder. */
export const LABEL_SYSTEM_FIELDS = [
  { key: 'system.user', label: 'Person (Kürzel)', type: 'text' },
  { key: 'system.company', label: 'Firmenname', type: 'text' },
  { key: 'system.web', label: 'Web-Adresse', type: 'text' },
] as const;
/** Anlass-Felder des Wareneingangs (nur Artikel-Etiketten). */
export const LABEL_RECEIPT_FIELDS = [
  { key: 'receipt.order_number', label: 'Bestellnummer', type: 'text' },
  { key: 'receipt.supplier', label: 'Lieferant', type: 'text' },
  { key: 'receipt.date', label: 'Eingangsdatum', type: 'date' },
  { key: 'receipt.quantity', label: 'gebuchte Menge', type: 'quantity' },
] as const;
export const LABEL_FIELD_GROUPS = { system: 'Datum & System', receipt: 'Wareneingang' } as const;

/**
 * Beschriftung der Strichcode-Formate im Editor (Story 27.5): eine Tabelle über alle `labelBarcodeFormats`, damit 27.4
 * neue Formate nur hier und im Schema ergänzt (der Typ verlangt einen Eintrag je Format).
 */
export const labelBarcodeFormatLabels: Record<LabelBarcodeFormat, { label: string; hint: string }> = {
  code128: { label: 'Code 128', hint: 'automatisch A/B/C' },
  ean13: { label: 'EAN-13', hint: '13 Ziffern mit Prüfziffer' },
  ean8: { label: 'EAN-8', hint: 'Kleine Packungen, 8 Ziffern' },
  upca: { label: 'UPC-A', hint: 'US-Ware, 12 Ziffern' },
  gs1_128: { label: 'GS1-128', hint: 'Logistik mit Datenbezeichnern, z. B. (01) GTIN' },
  itf14: { label: 'ITF-14', hint: 'Umkartons, GTIN-14' },
  code39: { label: 'Code 39', hint: 'Ältere Scanner, Großbuchstaben und Ziffern' },
  qrcode: { label: 'QR-Code', hint: 'Shop-Link oder Datenblatt' },
  datamatrix: { label: 'DataMatrix', hint: 'Sehr kleine Teile' },
  gs1_datamatrix: { label: 'GS1-DataMatrix', hint: 'Kleine Teile mit GTIN' },
};

/** Schreibweisen des Datum-Bausteins mit deutscher Beschriftung (Editor, Story 27.5). */
export const labelDateFormatLabels: Record<LabelDateFormat, string> = {
  'dd.MM.yyyy': 'TT.MM.JJJJ',
  'yyyy-MM-dd': 'JJJJ-MM-TT',
  week: 'Kalenderwoche',
  'MM/yyyy': 'MM/JJJJ',
  yyMMdd: 'JJMMTT (GS1)',
};
